import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Phone, Mail, Menu, X, ShieldCheck } from "lucide-react";
import { SITE, NAV_LINKS } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `px-1 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "text-brand-navy border-b-2 border-brand-orange"
        : "text-slate-700 hover:text-brand-navy"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white" data-testid="site-header">
      {/* Top contact bar */}
      <div className="bg-brand-navy text-white">
        <div className="container-x flex flex-col items-start justify-between gap-1 py-2 text-xs sm:flex-row sm:items-center sm:text-sm">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <a
              href={SITE.phoneHref}
              className="inline-flex items-center gap-2 hover:text-brand-orange"
              data-testid="topbar-phone-link"
            >
              <Phone size={14} aria-hidden="true" />
              <span>{SITE.phone}</span>
            </a>
            <a
              href={SITE.emailHref}
              className="inline-flex items-center gap-2 hover:text-brand-orange"
              data-testid="topbar-email-link"
            >
              <Mail size={14} aria-hidden="true" />
              <span>{SITE.email}</span>
            </a>
          </div>
          <div className="hidden text-white/80 sm:block" data-testid="topbar-service-area">
            Serving all of Nova Scotia · Mon–Fri 9:00–5:00
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="border-b border-slate-200 bg-white">
        <div className="container-x flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={SITE.logoUrl}
              alt="Maritime Thermal Solutions logo"
              className="h-12 w-36 object-contain"
            />
          </Link>

          <nav className="hidden items-center gap-6 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={linkClass}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/verify"
              className="ml-2 inline-flex items-center gap-2 rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-orange-hover"
              data-testid="header-verify-cta"
            >
              <ShieldCheck size={16} aria-hidden="true" />
              Verify a Representative
            </Link>
          </nav>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md p-2 text-slate-700 hover:bg-slate-100 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-menu">
            <div className="container-x flex flex-col gap-1 py-3">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-2 text-sm font-medium ${
                      isActive
                        ? "bg-slate-100 text-brand-navy"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/verify"
                onClick={() => setOpen(false)}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-brand-orange px-4 py-2 text-sm font-semibold text-white"
                data-testid="mobile-verify-cta"
              >
                <ShieldCheck size={16} aria-hidden="true" />
                Verify a Representative
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
